import { CSSInterpolation } from '@emotion/css/create-instance'
import { css } from '@emotion/react'
import React from 'react'

import { UnstyledAnchor } from '../../components/UnstyledAnchor'
import type { Icon16 } from './FoundationIcon/icons/index'
import { Markdown, TextRenderer } from './Markdown'
import {
  RowUtils,
  type StartElemIconProps,
  type StartElemNoneProps,
  type StartElemNumberPogProps,
  type StartElemPogProps,
} from './Rows/RowUtils'
import { TextButton } from './TextButton'
import { Type } from './Type'

const styles = {
  metadataContainer: css({
    textAlign: 'right',
  }),
}

type Props = (
  | StartElemIconProps
  | StartElemPogProps
  | StartElemNumberPogProps
  | StartElemNoneProps
) & {
  /**
   * Leading main text.
   */
  title: string
  titleSize?: 'S' | 'M'
  /**
   * Leading secondary text.
   */
  caption?: string
  /**
   * Leading secondary text with markdown support.
   */
  markdownCaption?: string
  /**
   * Trailing main text.
   */
  metadata?: string
  /**
   * Trailing secondary text.
   */
  secondaryMetadata?: string
  /**
   * Disables hover, click interactions. Note that when set to
   * true, onClick will not be called. False by default.
   */
  isDisabled?: boolean
  overrideRootStyles?: CSSInterpolation
  /**
   * Controls divider at the bottom of the NavigationRow.
   * True by default.
   */
  hasDivider?: boolean
  /**
   * Places an alert icon next to the trailing call to action.
   * False by default.
   */
  hasAlert?: boolean
  'aria-label'?: React.HTMLAttributes<HTMLDivElement>['aria-label']
  'aria-describedby'?: React.HTMLAttributes<HTMLDivElement>['aria-describedby']
} & (
    | {
        /**
         * Determines call-to-action in the trailing portion of the
         * NavigationRow.
         */
        ctaType: 'buttonLink'
        ctaButtonLinkText: string
      }
    | {
        // NOTE(omer.demirkan): Because there's a default cta of
        // a right caret icon, these are optional.
        /**
         * Places a trailing icon on the NavigationRow.
         */
        ctaType?: 'icon'
        /**
         * Allows to override the default CTA icon.
         */
        ctaIcon?: Icon16
      }
  ) &
  (
    | { action: 'link'; href: string; newTab?: boolean }
    | {
        action: 'click'
        // NOTE(omer.demirkan): In case redirection is handled in a parent element.
        // click event.
        /**
         * Called when either a click, or 'Enter' is pressed while focused occurs.
         */
        onClick?: React.MouseEventHandler<HTMLDivElement>
      }
  )

/**
 * A Row intended to be used to transition to a new
 * view, be it a new page, a modal, etc.
 *
 * If you plan on directing the user to an input, see
 * SettingsRow.
 */
export function NavigationRow(props: Props) {
  // NOTE(omer.demirkan): Destructuring here because of union
  // discriminator issues.
  const {
    'aria-describedby': ariaDescribedBy,
    'aria-label': ariaLabel,
    caption,
    ctaType,
    hasAlert = false,
    hasDivider = true,
    isDisabled = false,
    markdownCaption,
    metadata,
    overrideRootStyles,
    secondaryMetadata,
    title,
    titleSize = 'M',
    ...leadingContentProps
  } = props

  const { colors } = RowUtils.useRowColors(isDisabled)

  const hasMetadata = !!(metadata || secondaryMetadata)

  return (
    <UnstyledAnchor
      href={!isDisabled && props.action === 'link' ? props.href : undefined}
      rel={
        props.action === 'link' && props.newTab
          ? 'noopener noreferrer'
          : undefined
      }
      target={props.action === 'link' && props.newTab ? '_blank' : undefined}
    >
      <div
        aria-describedby={ariaDescribedBy}
        aria-disabled={isDisabled}
        aria-label={ariaLabel || title || undefined}
        css={overrideRootStyles}
      >
        <RowUtils.Container
          isDisabled={isDisabled}
          isInteractive
          isTabbable={props.action === 'click'}
          onClick={props.action === 'click' ? props.onClick : undefined}
        >
          <RowUtils.Section
            divider={hasDivider && 'bottom'}
            // NOTE(omer.demirkan): RowUtils.Icon's margins aren't responsive
            // to child pseudoselector. This pattern of wrapping in
            // divs is recurring throughout this component.
            leadingContent={
              <RowUtils.LeadingContent
                isDisabled={isDisabled}
                {...leadingContentProps}
              />
            }
            mainContent={
              <>
                <Type.Text color={colors.primaryText} element="h6" size={titleSize}>
                  {title}
                </Type.Text>
                {caption && (
                  <Type.Text color={colors.secondaryText} maxLines={3} size="S">
                    {caption}
                  </Type.Text>
                )}
                {markdownCaption && (
                  <Markdown
                    overrideRenderers={{
                      paragraph: markdownProps => (
                        <TextRenderer {...markdownProps} />
                      ),
                      text: string => (
                        <Type.Text
                          color={colors.secondaryText}
                          maxLines={3}
                          overrideRootStyles={{
                            margin: 0,
                          }}
                          size="S"
                        >
                          {string}
                        </Type.Text>
                      ),
                    }}
                    source={markdownCaption}
                  />
                )}
              </>
            }
            trailingContent={
              <>
                {(metadata || secondaryMetadata || hasAlert) && (
                  <div css={RowUtils.contentGroupStyles}>
                    {metadata || secondaryMetadata ? (
                      <div css={styles.metadataContainer}>
                        {metadata && (
                          <Type.Text
                            color={colors.primaryText}
                            element="h6"
                            size="M"
                          >
                            {metadata}
                          </Type.Text>
                        )}
                        {secondaryMetadata && (
                          <Type.Text
                            color={colors.secondaryText}
                            element="p"
                            size="S"
                          >
                            {secondaryMetadata}
                          </Type.Text>
                        )}
                      </div>
                    ) : null}
                    {hasAlert && (
                      <RowUtils.Icon
                        color={colors.icon}
                        type={hasMetadata ? 'Alert16' : 'Alert24'}
                      />
                    )}
                  </div>
                )}
                <div>
                  {props.ctaType === 'buttonLink' ? (
                    <TextButton
                      color={colors.primaryText}
                      label={props.ctaButtonLinkText}
                      overrideRootStyles={isDisabled && { cursor: 'auto' }}
                      size="M"
                      tabIndex={-1}
                      type="button"
                      variant="standalone"
                    />
                  ) : (
                    <RowUtils.Icon
                      color={colors.caret}
                      type={props.ctaIcon || 'CaretRight16'}
                    />
                  )}
                </div>
              </>
            }
          />
        </RowUtils.Container>
      </div>
    </UnstyledAnchor>
  )
}
